import {
  MARKET_DATA_TRADE,
  formattedDateForSubscription,
  logger,
  request,
  useEndpointsContext,
  useObservable,
  useSubscription,
  type MarketDataTrade,
  type RequestStream,
} from '@talos/kyoko';
import { sub } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { concat, from, of } from 'rxjs';
import { map, scan } from 'rxjs/operators';

export function useMarketDataTrade({
  symbol,
  markets,
  limit = 100,
  withHistoricalTrades = true,
  tag,
}: {
  symbol: string;
  markets: string[];
  limit?: number;
  withHistoricalTrades?: boolean;
  tag: string;
}) {
  const [marketDataTradeRequest, setMarkedDataTradeRequest] = useState<MarketDataTradeReq | null>(null);
  const { apiEndpoint } = useEndpointsContext();
  const endpoint = useMemo(
    () => `${apiEndpoint}/symbols/${symbol}/markets/${markets.join(',')}/trades`,
    [symbol, markets, apiEndpoint]
  );
  const { data: subscription, error: errorObservable } = useSubscription(marketDataTradeRequest);

  const latestHistoricalTrades = useCallback(
    (limit: number) => {
      if (withHistoricalTrades) {
        const queryString = new URLSearchParams({
          limit: limit.toString(),
          orderBy: '-TransactTime',
          // startDate & endDate are required due to [sc-67443]
          startDate: formattedDateForSubscription(sub(new Date(), { hours: 24 })),
          endDate: formattedDateForSubscription(new Date()),
        }).toString();
        try {
          return request('GET', endpoint + '?' + queryString).catch(() => of({ data: [] }));
        } catch (e) {
          logger.error(e as Error);
          return of({ data: [] });
        }
      } else {
        return of({ data: [] });
      }
    },
    [endpoint, withHistoricalTrades]
  );

  const observable = useObservable(
    () =>
      concat(from(latestHistoricalTrades(limit)), subscription).pipe(
        map(res => res.data),
        scan((result, data: MarketDataTrade[]) => {
          data &&
            data.forEach(trade => {
              result.set(trade.TradeID, trade);
            });
          const arr = [...result.values()]
            .toSorted((a: MarketDataTrade, b: MarketDataTrade) => {
              return new Date(b.TransactTime).getTime() - new Date(a.TransactTime).getTime();
            })
            .slice(0, limit);
          result = new Map(
            arr.map(trade => {
              return [trade.TradeID, trade];
            })
          );
          return result;
        }, new Map<string, MarketDataTrade>()),
        map(tradesMap => {
          return [...tradesMap.values()];
        })
      ),
    [subscription, latestHistoricalTrades, limit]
  );

  useEffect(() => {
    if (symbol && markets) {
      setMarkedDataTradeRequest({
        name: MARKET_DATA_TRADE,
        tag: `${tag}/useMarketDataTrade`,
        Symbol: symbol,
        Markets: markets,
      });
    }
    return () => setMarkedDataTradeRequest(null);
  }, [symbol, markets, limit, latestHistoricalTrades, subscription, tag]);

  const marketDataTradeObs = useObservable(
    () =>
      observable.pipe(
        map((trades: MarketDataTrade[]) => {
          return { initial: true, data: trades };
        })
      ),
    [observable]
  );

  return { marketDataTradeObs, errorObservable };
}

interface MarketDataTradeReq extends RequestStream {
  name: string;
  Symbol: string;
  Markets: string[];
}
